import { useState, type FormEvent } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  Card,
  CardBody,
  Drawer,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  SkeletonText,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useAuditLog } from "@/lib/queries";
import { formatDateTime } from "@/lib/utils/format";
import type { AuditLogEntry, AuditLogFilters } from "@/lib/api";

const PAGE_SIZE = 25;

export function AuditPage() {
  const [filters, setFilters] = useState<AuditLogFilters>({ page: 1, pageSize: PAGE_SIZE });
  const [draft, setDraft] = useState<AuditLogFilters>({});
  const [detailEntry, setDetailEntry] = useState<AuditLogEntry | null>(null);

  const auditQuery = useAuditLog(filters);
  const data = auditQuery.data;
  const entries = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.pageSize || PAGE_SIZE))) : 1;

  function handleFilterSubmit(event: FormEvent) {
    event.preventDefault();
    setFilters({ ...draft, page: 1, pageSize: PAGE_SIZE });
  }

  function handleReset() {
    setDraft({});
    setFilters({ page: 1, pageSize: PAGE_SIZE });
  }

  function goToPage(page: number) {
    setFilters((prev) => ({ ...prev, page }));
  }

  return (
    <div>
      <PageHeader title="Auditoria" description="Registro inmutable de toda mutacion administrativa." />

      <Card className="mb-4">
        <CardBody>
          <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Actor">
              {(field) => (
                <Input {...field} value={draft.actor ?? ""} onChange={(e) => setDraft({ ...draft, actor: e.target.value || undefined })} />
              )}
            </FormField>
            <FormField label="Accion">
              {(field) => (
                <Input {...field} value={draft.action ?? ""} onChange={(e) => setDraft({ ...draft, action: e.target.value || undefined })} />
              )}
            </FormField>
            <FormField label="Tipo de entidad">
              {(field) => (
                <Input
                  {...field}
                  value={draft.entityType ?? ""}
                  onChange={(e) => setDraft({ ...draft, entityType: e.target.value || undefined })}
                />
              )}
            </FormField>
            <FormField label="Id de entidad">
              {(field) => (
                <Input
                  {...field}
                  value={draft.entityId ?? ""}
                  onChange={(e) => setDraft({ ...draft, entityId: e.target.value || undefined })}
                />
              )}
            </FormField>
            <FormField label="Desde">
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={draft.dateFrom ?? ""}
                  onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                />
              )}
            </FormField>
            <FormField label="Hasta">
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={draft.dateTo ?? ""}
                  onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                />
              )}
            </FormField>
            <FormField label="Request id">
              {(field) => (
                <Input
                  {...field}
                  value={draft.requestId ?? ""}
                  onChange={(e) => setDraft({ ...draft, requestId: e.target.value || undefined })}
                />
              )}
            </FormField>
            <div className="flex items-end gap-2">
              <Button type="submit" icon={<Search className="h-4 w-4" aria-hidden="true" />}>
                Filtrar
              </Button>
              <Button type="button" variant="ghost" onClick={handleReset}>
                Limpiar
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {auditQuery.isLoading && (
            <div className="p-5">
              <SkeletonText lines={6} />
            </div>
          )}
          {auditQuery.isError && (
            <div className="p-5">
              <ErrorState description="No se pudo cargar el registro de auditoria." onRetry={() => auditQuery.refetch()} />
            </div>
          )}
          {auditQuery.isSuccess && entries.length === 0 && (
            <EmptyState title="Sin resultados" description="No hay eventos de auditoria con estos filtros." />
          )}
          {entries.length > 0 && (
            <>
              <TableContainer className="rounded-none border-0">
                <Table>
                  <THead>
                    <tr>
                      <TH>Fecha</TH>
                      <TH>Actor</TH>
                      <TH>Accion</TH>
                      <TH>Entidad</TH>
                      <TH>IP</TH>
                      <TH className="text-right">Detalle</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {entries.map((entry) => (
                      <TR key={entry.id}>
                        <TD className="whitespace-nowrap">{formatDateTime(entry.occurredAt)}</TD>
                        <TD>{entry.actorEmail ?? "-"}</TD>
                        <TD className="font-mono text-xs">{entry.action}</TD>
                        <TD>
                          {entry.entityType}
                          {entry.entityId && <span className="text-ink-faint"> #{entry.entityId}</span>}
                        </TD>
                        <TD>{entry.ipAddress ?? "-"}</TD>
                        <TD>
                          <div className="flex justify-end">
                            <Button variant="ghost" size="sm" onClick={() => setDetailEntry(entry)}>
                              Ver detalle
                            </Button>
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableContainer>

              <div className="flex items-center justify-between border-t border-ink/5 px-4 py-3">
                <p className="text-xs text-ink-faint">
                  Pagina {data?.page ?? 1} de {totalPages} - {data?.total ?? 0} eventos
                </p>
                <div className="flex gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(filters.page ?? 1) <= 1}
                    onClick={() => goToPage((filters.page ?? 1) - 1)}
                    aria-label="Pagina anterior"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(filters.page ?? 1) >= totalPages}
                    onClick={() => goToPage((filters.page ?? 1) + 1)}
                    aria-label="Pagina siguiente"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <Drawer
        open={detailEntry !== null}
        onClose={() => setDetailEntry(null)}
        title={detailEntry ? `${detailEntry.action}` : "Detalle"}
      >
        {detailEntry && <AuditDetail entry={detailEntry} />}
      </Drawer>
    </div>
  );
}

function AuditDetail({ entry }: { entry: AuditLogEntry }) {
  return (
    <div className="space-y-4 text-sm">
      <dl className="grid grid-cols-2 gap-3">
        <div>
          <dt className="text-xs text-ink-faint">Fecha</dt>
          <dd className="text-ink">{formatDateTime(entry.occurredAt)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-faint">Actor</dt>
          <dd className="text-ink">{entry.actorEmail ?? "-"}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-faint">Entidad</dt>
          <dd className="text-ink">
            {entry.entityType} {entry.entityId ?? ""}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-ink-faint">IP</dt>
          <dd className="text-ink">{entry.ipAddress ?? "-"}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs text-ink-faint">User agent</dt>
          <dd className="break-all text-ink">{entry.userAgent ?? "-"}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs text-ink-faint">Request id</dt>
          <dd className="break-all font-mono text-xs text-ink">{entry.requestId ?? "-"}</dd>
        </div>
      </dl>

      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">Antes</p>
        <pre className="scrollbar-thin overflow-x-auto rounded-lg bg-surface-subtle p-3 text-xs text-ink-muted">
          {entry.before ? JSON.stringify(entry.before, null, 2) : "null"}
        </pre>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">Despues</p>
        <pre className="scrollbar-thin overflow-x-auto rounded-lg bg-surface-subtle p-3 text-xs text-ink-muted">
          {entry.after ? JSON.stringify(entry.after, null, 2) : "null"}
        </pre>
      </div>
    </div>
  );
}
